import { Controller } from "@hotwired/stimulus";

/**
 * Stimulus controller for text inputs with variable autocomplete.
 * Type "@" to trigger a dropdown of available variables.
 *
 * Expects `variables` value to be an array of objects with:
 *   - variable: The variable key (e.g., "ticket.id")
 *   - name: Display name (e.g., "Ticket ID")
 *   - help_text: Description shown in dropdown
 *
 * Used by the _autocomplete_input.html.erb partial.
 */

const DROPDOWN_CLASSES =
  "absolute z-50 mt-1 w-96 bg-white border border-input rounded-md shadow-md max-h-60 overflow-y-auto p-1";
const ITEM_CLASSES =
  "px-2 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm";
const SELECTED_CLASSES = ["bg-accent", "text-accent-foreground"];
const BADGE_CLASSES =
  "inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-brand-50 text-brand-700 border border-brand-500 mx-0.5";
const BADGE_INVERSE_CLASSES =
  "inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold bg-brand-700 text-brand-50 border border-brand-700 mx-0.5";
const INVALID_CLASSES = "text-red-600";
const BLUR_DELAY = 300; // Delay before removing dropdown on blur to allow click events

export default class AutocompleteController extends Controller {
  static targets = ["input", "preview", "previewContent", "previewLabel"];
  static values = {
    variables: Array,
    defaultPreview: String,
    showPreview: { type: Boolean, default: true },
    multiline: { type: Boolean, default: false },
    displayFormat: String,
    previewPrefix: String,
  };

  connect() {
    this.dropdown = null;
    this.currentSuggestions = [];
    this.selectedIndex = 0;
    this.searchStart = -1;
    this.convertToAtSyntax();
    if (this.showPreviewValue) {
      this.updatePreview();
    }
  }

  disconnect() {
    this.removeDropdown();
  }

  convertToAtSyntax() {
    const value = this.inputTarget.value;
    if (value) this.inputTarget.value = value.replace(/\{\{\s*([\w.]+)\s*\}\}/g, "@$1");
  }

  onInput() {
    if (this.showPreviewValue) {
      this.updatePreview();
    }
    this.updateDropdown();
  }

  updateDropdown() {
    const { atIndex, searchText } = this.getSearchContext();

    if (!this.shouldShowDropdown(atIndex, searchText)) {
      this.removeDropdown();
      return;
    }

    this.searchStart = atIndex;
    this.createDropdown(this.filterVariables(searchText.toLowerCase()));
  }

  getSearchContext() {
    const beforeCursor = this.inputTarget.value.substring(0, this.inputTarget.selectionStart);
    const atIndex = beforeCursor.lastIndexOf("@");
    const searchText = beforeCursor.substring(atIndex + 1);
    return { atIndex, searchText };
  }

  shouldShowDropdown(atIndex, searchText) {
    return atIndex !== -1 && !/\s/.test(searchText);
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return;

    const value = this.inputTarget.value;
    this.previewTarget.style.display = "block";

    if (!value || value.trim() === "") {
      this.showDefaultPreview();
    } else if (this.displayFormatValue !== "list" && value.includes("@")) {
      this.showVariablePreview(value);
    } else {
      this.showPlainTextPreview(value);
    }
  }

  showDefaultPreview() {
    if (this.hasDefaultPreviewValue && this.defaultPreviewValue) {
      this.setPreviewLabel("Default: ");
      if (this.displayFormatValue === "list") {
        this.renderListPreview(this.defaultPreviewValue, { includePrefix: false });
      } else {
        this.renderPreviewWithBadges(this.defaultPreviewValue);
      }
    } else {
      // Hide the preview entirely when there's no default
      this.previewTarget.style.display = "none";
    }
  }

  showPlainTextPreview(value) {
    this.setPreviewLabel("Preview: ");
    if (this.displayFormatValue === "list") {
      this.renderListPreview(value, { includePrefix: true });
    } else if (this.multilineValue) {
      this.previewContentTarget.innerHTML = "";
      this.appendText(value);
    } else {
      this.previewContentTarget.textContent = value;
    }
  }

  showVariablePreview(value) {
    this.setPreviewLabel("Preview: ");
    this.renderPreviewWithBadges(value);
  }

  setPreviewLabel(text) {
    this.previewLabelTarget.innerHTML = this.multilineValue ? text + "<br>" : text;
  }

  renderPreviewWithBadges(value) {
    this.previewContentTarget.innerHTML = "";

    value.split(/(@[\w.]+|\{\{\s*[\w.]+\s*\}\})/).forEach((part) => {
      if (!part) return;

      const variableName = this.extractVariableName(part);

      if (variableName) {
        this.appendVariableBadge(variableName, part);
      } else {
        this.appendText(part);
      }
    });
  }

  renderListPreview(value, { includePrefix = true } = {}) {
    this.previewContentTarget.innerHTML = "";

    const prefixItems =
      includePrefix && this.hasPreviewPrefixValue
        ? this.previewPrefixValue
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t)
        : [];
    const userItems = value
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);
    const items = [...prefixItems, ...userItems];

    items.forEach((item, index) => {
      if (index > 0) this.previewContentTarget.appendChild(document.createTextNode(" "));

      const isPureVariable = /^\s*(@[\w.]+|\{\{\s*[\w.]+\s*\}\})\s*$/.test(item);

      if (isPureVariable) {
        const variableName = this.extractVariableName(item);
        const variable = variableName
          ? this.variablesValue.find((v) => v.variable === variableName)
          : null;

        const badge = document.createElement("span");
        if (variable) {
          badge.className = BADGE_INVERSE_CLASSES;
          badge.textContent = variable.name;
        } else {
          badge.className = BADGE_CLASSES;
          badge.textContent = item;
        }
        this.previewContentTarget.appendChild(badge);
        return;
      }

      const badge = document.createElement("span");
      badge.className = BADGE_CLASSES;
      badge.textContent = item;
      this.previewContentTarget.appendChild(badge);
    });
  }

  extractVariableName(text) {
    if (text.startsWith("@")) return text.substring(1);
    const match = text.match(/\{\{\s*([\w.]+)\s*\}\}/);
    return match ? match[1] : null;
  }

  appendVariableBadge(variableName, originalText) {
    const variable = this.variablesValue.find((v) => v.variable === variableName);
    const span = document.createElement("span");

    if (variable) {
      span.className = BADGE_CLASSES;
      span.textContent = variable.name;
    } else {
      span.className = INVALID_CLASSES;
      span.textContent = originalText;
    }

    this.previewContentTarget.appendChild(span);
  }

  appendText(text) {
    const lines = text.split("\n");
    lines.forEach((line, index) => {
      if (index > 0) {
        this.previewContentTarget.appendChild(document.createElement("br"));
      }
      if (line) {
        const span = document.createElement("span");
        span.textContent = line;
        this.previewContentTarget.appendChild(span);
      }
    });
  }

  onKeydown(e) {
    if (!this.dropdown) return;

    const items = this.dropdown.querySelectorAll("[data-item]");

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.moveSelection(items, 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.moveSelection(items, -1);
        break;
      case "Enter":
        e.preventDefault();
        this.currentSuggestions[this.selectedIndex] &&
          this.selectVariable(this.currentSuggestions[this.selectedIndex]);
        break;
      case "Escape":
        e.preventDefault();
        this.removeDropdown();
        break;
    }
  }

  onBlur() {
    setTimeout(() => this.removeDropdown(), BLUR_DELAY);
  }

  filterVariables(searchText) {
    return this.variablesValue.filter(
      (v) =>
        v.name.toLowerCase().includes(searchText) || v.variable.toLowerCase().includes(searchText)
    );
  }

  createDropdown(suggestions) {
    this.removeDropdown();
    if (suggestions.length === 0) return;

    this.currentSuggestions = suggestions;
    this.dropdown = document.createElement("div");
    this.dropdown.className = DROPDOWN_CLASSES;
    this.dropdown.style.cssText = `left: 0; top: ${this.inputTarget.offsetHeight}px`;

    suggestions.forEach((variable, index) => {
      const item = document.createElement("div");
      item.dataset.item = "";
      item.className = `${ITEM_CLASSES} ${index === 0 ? SELECTED_CLASSES.join(" ") : ""}`;
      item.innerHTML = `
        <div class="font-medium text-sm">${variable.name}</div>
        <div class="text-xs text-muted-foreground">${variable.help_text}</div>
      `;
      item.addEventListener("mousedown", () => this.selectVariable(variable));
      this.dropdown.appendChild(item);
    });

    this.inputTarget.parentElement.style.position = "relative";
    this.inputTarget.parentElement.appendChild(this.dropdown);
  }

  removeDropdown() {
    this.dropdown?.remove();
    this.dropdown = null;
    this.selectedIndex = 0;
  }

  selectVariable(variable) {
    if (this.searchStart === -1) return;

    const searchStart = this.searchStart;
    const before = this.inputTarget.value.substring(0, searchStart);
    const after = this.inputTarget.value.substring(this.inputTarget.selectionStart);
    const newValue = `${before}@${variable.variable}${after}`;
    const cursorPos = before.length + variable.variable.length + 1;

    this.searchStart = -1;
    this.inputTarget.value = newValue;
    this.removeDropdown();
    this.inputTarget.setSelectionRange(cursorPos, cursorPos);
    this.inputTarget.focus();
    if (this.showPreviewValue) {
      this.updatePreview();
    }
  }

  moveSelection(items, direction) {
    items[this.selectedIndex]?.classList.remove(...SELECTED_CLASSES);
    this.selectedIndex = (this.selectedIndex + direction + items.length) % items.length;
    items[this.selectedIndex]?.classList.add(...SELECTED_CLASSES);
    items[this.selectedIndex]?.scrollIntoView({ block: "nearest" });
  }
}
